import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Gift, Timer } from "lucide-react";
import { services } from "../lib/services";
import { brl } from "../lib/format";

const serviceById = (id) => services.find((s) => s.id === id);

function useRemaining(expiresAt) {
  const calc = () => Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  const [remaining, setRemaining] = useState(calc);
  useEffect(() => {
    const timer = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);
  return remaining;
}

const fmtRemaining = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
  const sec = s % 60;
  return `${m}min ${String(sec).padStart(2, "0")}s`;
};

export default function Benefits({ user, benefits, onOrder }) {
  const remaining = useRemaining(benefits.expires_at);
  const active = remaining > 0 && benefits.offers.length > 0;
  const firstName = user.name.split(" ")[0];

  return (
    <section className="benefits section-pad" data-testid="benefits-section">
      <motion.div initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
        <div className="eyebrow"><i /> OFERTAS DE BOAS-VINDAS</div>
        <div className="benefits-head">
          <h2 data-testid="benefits-greeting">Olá, {firstName}.<br /><em>Benefícios exclusivos</em> para você.</h2>
          <div className="benefits-meta">
            {benefits.credit > 0 ? (
              <div className="credit-badge" data-testid="credit-badge"><Gift size={15} /> Seu crédito: <b>{brl(benefits.credit)}</b></div>
            ) : benefits.credit_used ? (
              <div className="credit-badge used" data-testid="credit-badge"><Check size={15} /> Crédito de boas-vindas utilizado</div>
            ) : null}
            {active && (
              <div className="bc-countdown" data-testid="benefits-countdown">
                <Timer size={14} /> Suas ofertas valem por 48h · Tempo restante: <b>{fmtRemaining(remaining)}</b>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {active ? (
        <div className="offer-grid">
          {benefits.offers.map((offer, index) => (
            <motion.article key={offer.service_id} className={`offer-card offer-${offer.service_id}`}
              data-testid={`offer-card-${offer.service_id}`}
              initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}>
              {offer.kind === "bogo" ? (
                <>
                  <div className="offer-tag">OFERTA ESPECIAL · NOVOS CLIENTES</div>
                  <h3>{offer.name}</h3>
                  <p className="offer-headline">{offer.headline}.</p>
                  <p className="offer-credit">Válido para o seu primeiro serviço de edição avulsa.</p>
                </>
              ) : (
                <>
                  <div className="offer-tag">BENEFÍCIO DE PRIMEIRO LOGIN</div>
                  <h3>{offer.name}</h3>
                  <div className="offer-prices" data-testid={`offer-price-${offer.service_id}`}>
                    {offer.savings > 0 ? (
                      <>
                        <s>{brl(offer.price)}/mês</s>
                        <strong>{brl(offer.discounted)}/mês</strong>
                      </>
                    ) : (
                      <strong>{brl(offer.price)}/mês</strong>
                    )}
                  </div>
                  {offer.savings > 0 ? (
                    <p className="offer-credit" data-testid={`offer-credit-${offer.service_id}`}>
                      Você economiza {brl(offer.savings)} com o seu crédito de boas-vindas.
                    </p>
                  ) : (
                    <p className="offer-credit">Crédito de boas-vindas já utilizado.</p>
                  )}
                </>
              )}
              <button className="button button-primary offer-btn" onClick={() => onOrder(serviceById(offer.service_id))}
                data-testid={`offer-redeem-${offer.service_id}`}>
                {offer.kind === "bogo" ? "Aproveitar oferta" : "Contratar agora"} <ArrowUpRight size={15} />
              </button>
            </motion.article>
          ))}
        </div>
      ) : (
        <p className="offers-expired" data-testid="offers-expired">
          Suas ofertas de boas-vindas expiraram — todos os serviços continuam disponíveis abaixo, com as mesmas condições de sempre.
        </p>
      )}
    </section>
  );
}
